import React, { useEffect, useState, createContext, useContext } from 'react';
import { Routes, Route } from 'react-router-dom';
import AppLayout from '@cloudscape-design/components/app-layout';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Spinner from '@cloudscape-design/components/spinner';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Select, { SelectProps } from '@cloudscape-design/components/select';
import Home from './Home';
import PaymentPage from './PaymentPage';
import { REGIONS, DEFAULT_STAGE, DEFAULT_REGION, getApiBase, getDeployedRegions } from '../constants';
import {
  getLoginUrl,
  getStoredToken,
  setStoredTokens,
  clearStoredTokens,
  isTokenExpired,
  getUserFromToken,
  exchangeCodeForTokens,
} from '../auth';
import fintechIcon from '../assets/fintech-logo.png';
import '../css/app.css';

interface AppContextType {
  token: string;
  apiBase: string;
  region: string;
  stage: string;
}

export const AppContext = createContext<AppContextType>({
  token: '', apiBase: '', region: DEFAULT_REGION, stage: DEFAULT_STAGE,
});
export const useAppContext = () => useContext(AppContext);

const AppHeader: React.FC<{
  user: ReturnType<typeof getUserFromToken>;
  selectedRegion: SelectProps.Option;
  onRegionChange: (r: SelectProps.Option) => void;
}> = ({ user, selectedRegion, onRegionChange }) => (
  <div className="app-header">
    <div className="app-header__brand">
      <img src={fintechIcon} alt="Beacon" className="app-header__logo" />
      <div>
        <div className="app-header__title">Last Mile Beacon</div>
        <div className="app-header__subtitle">Payment Lifecycle Tracking</div>
      </div>
    </div>
    {user && (
      <div className="app-header__right">
        <div className="app-header__region-select">
          <Select
            selectedOption={selectedRegion}
            onChange={({ detail }) => onRegionChange(detail.selectedOption)}
            options={getDeployedRegions(DEFAULT_STAGE)}
          />
        </div>
        <div className="app-header__user">
          <div className="app-header__user-name">{user.name || user.alias}</div>
          <div className="app-header__user-alias">{user.alias} · {user.email}</div>
          {user.groups.length > 0 && (
            <div className="app-header__groups">
              {user.groups.slice(0, 3).map(g => (
                <span key={g} className="app-header__group-badge">{g}</span>
              ))}
              {user.groups.length > 3 && (
                <span className="app-header__group-more">+{user.groups.length - 3}</span>
              )}
            </div>
          )}
        </div>
      </div>
    )}
  </div>
);

const App = () => {
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState<SelectProps.Option>(REGIONS[0]);

  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      if (code) {
        try {
          const tokens = await exchangeCodeForTokens(code);
          setStoredTokens(tokens.id_token);
          setToken(tokens.id_token);
          const returnUrl = sessionStorage.getItem('beacon_return_url') || '/';
          sessionStorage.removeItem('beacon_return_url');
          const safeUrl = (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) ? returnUrl : '/';
          window.history.replaceState({}, '', safeUrl);
          setLoading(false);
        } catch (e) {
          console.error('Token exchange failed', e);
          clearStoredTokens();
          window.history.replaceState({}, '', window.location.pathname);
          setAuthError('Token exchange failed. Please try again.');
          setLoading(false);
        }
        return;
      }
      const stored = getStoredToken();
      if (stored && !isTokenExpired(stored)) {
        setToken(stored);
        setLoading(false);
      } else {
        clearStoredTokens();
        sessionStorage.setItem('beacon_return_url',
          window.location.pathname + window.location.search + window.location.hash);
        window.location.href = await getLoginUrl();
      }
    };
    init().catch((e) => {
      console.error('Auth init failed', e);
      setAuthError('Authentication failed. Please try again.');
      setLoading(false);
    });
  }, []);

  const user = token ? getUserFromToken(token) : null;

  if (authError) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <SpaceBetween size="s" alignItems="center">
          <Box variant="p" color="text-status-error">{authError}</Box>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </SpaceBetween>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <SpaceBetween size="s" alignItems="center">
          <Spinner size="large" />
          <Box variant="p" color="text-body-secondary">Signing in via Amazon Federate...</Box>
        </SpaceBetween>
      </Box>
    );
  }

  const appContext: AppContextType = {
    token: token || '',
    apiBase: getApiBase(DEFAULT_STAGE, selectedRegion.value || DEFAULT_REGION),
    region: selectedRegion.value || DEFAULT_REGION,
    stage: DEFAULT_STAGE,
  };

  return (
    <AppContext.Provider value={appContext}>
      <AppLayout
        content={
          <ContentLayout
            header={
              <AppHeader
                user={user}
                selectedRegion={selectedRegion}
                onRegionChange={setSelectedRegion}
              />
            }
          >
            <Routes>
              <Route path="/payment" element={<PaymentPage />} />
              <Route path="*" element={<Home />} />
            </Routes>
          </ContentLayout>
        }
        navigationHide
        toolsHide
        maxContentWidth={Number.MAX_VALUE}
        disableContentPaddings={false}
      />
    </AppContext.Provider>
  );
};

export default App;
